import { useState, useMemo, useRef, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useLocale } from '../App'
import { DAPP_URL, MARKETPLACE_API_BASE } from '../content'

type TabId = 'overview' | 'pedigree' | 'performance' | 'health'

function MediaGallery({
  media,
  horseName,
  caption,
}: {
  media: MediaItem[]
  horseName: string
  caption?: string
}) {
  const [index, setIndex] = useState(0)
  const [fullscreen, setFullscreen] = useState(false)
  const [zoom, setZoom] = useState(1)
  const [showGrid, setShowGrid] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const current = media[index]
  const isVideo = current?.type === 'video'

  useEffect(() => {
    setIndex(0)
    setZoom(1)
  }, [media])

  useEffect(() => {
    if (!isVideo || !videoRef.current) return
    if (fullscreen) videoRef.current.play().catch(() => {})
  }, [index, fullscreen, isVideo])

  const goPrev = () => setIndex((i) => (i - 1 + media.length) % media.length)
  const goNext = () => setIndex((i) => (i + 1) % media.length)
  const zoomIn = () => setZoom((z) => Math.min(3, z + 0.25))
  const zoomOut = () => setZoom((z) => Math.max(0.5, z - 0.25))
  const playVideo = () => {
    if (current?.type === 'video' && videoRef.current) {
      videoRef.current.play().catch(() => {})
    }
  }

  if (media.length === 0) return null

  const content = (
    <div className="media-gallery">
      <div className="media-gallery-nav media-gallery-nav-left" onClick={goPrev} aria-label="上一张">
        <i className="fas fa-chevron-left" />
      </div>
      <div className="media-gallery-nav media-gallery-nav-right" onClick={goNext} aria-label="下一张">
        <i className="fas fa-chevron-right" />
      </div>
      <div className="media-gallery-index">
        {index + 1} / {media.length}
      </div>
      <div className="media-gallery-toolbar">
        <button type="button" className="media-gallery-btn" onClick={() => setShowGrid((g) => !g)} aria-label="网格">
          <i className="fas fa-th" />
        </button>
        <button type="button" className="media-gallery-btn" onClick={zoomOut} aria-label="缩小">
          <i className="fas fa-search-minus" />
        </button>
        <button type="button" className="media-gallery-btn" onClick={zoomIn} aria-label="放大">
          <i className="fas fa-search-plus" />
        </button>
        {isVideo && (
          <button type="button" className="media-gallery-btn" onClick={playVideo} aria-label="播放">
            <i className="fas fa-play" />
          </button>
        )}
        <button type="button" className="media-gallery-btn" onClick={() => setFullscreen(true)} aria-label="全屏">
          <i className="fas fa-expand" />
        </button>
      </div>
      <div className="media-gallery-viewport" style={{ transform: `scale(${zoom})` }}>
        {current?.type === 'image' && (
          <img src={current.url} alt={`${horseName} - ${index + 1}`} className="media-gallery-item" />
        )}
        {current?.type === 'video' && (
          <video
            ref={videoRef}
            src={current.url}
            className="media-gallery-item"
            controls
            playsInline
            muted={false}
          />
        )}
      </div>
      <div className="media-gallery-caption">
        {caption ?? `${horseName}`}
      </div>
      {showGrid && (
        <div className="media-gallery-grid-overlay" onClick={() => setShowGrid(false)}>
          <div className="media-gallery-grid-inner" onClick={(e) => e.stopPropagation()}>
            {media.map((m, i) => (
              <button
                key={i}
                type="button"
                className={`media-gallery-grid-item ${i === index ? 'active' : ''}`}
                onClick={() => { setIndex(i); setShowGrid(false); }}
              >
                {m.type === 'image' ? (
                  <img src={m.url} alt="" />
                ) : (
                  <span className="media-gallery-grid-video"><i className="fas fa-play" /></span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )

  if (fullscreen) {
    return (
      <div className="media-gallery-fullscreen" role="dialog" aria-modal="true">
        <button type="button" className="media-gallery-close" onClick={() => setFullscreen(false)} aria-label="关闭">
          <i className="fas fa-times" />
        </button>
        <div className="media-gallery-fullscreen-content">
          <div className="media-gallery-index">{(index + 1)} / {media.length}</div>
          <div className="media-gallery-nav media-gallery-nav-left" onClick={goPrev} aria-label="上一张">
            <i className="fas fa-chevron-left" />
          </div>
          <div className="media-gallery-nav media-gallery-nav-right" onClick={goNext} aria-label="下一张">
            <i className="fas fa-chevron-right" />
          </div>
          <div className="media-gallery-viewport" style={{ transform: `scale(${zoom})` }}>
            {current?.type === 'image' && (
              <img src={current.url} alt={`${horseName} - ${index + 1}`} className="media-gallery-item" />
            )}
            {current?.type === 'video' && (
              <video ref={videoRef} src={current.url} className="media-gallery-item" controls playsInline />
            )}
          </div>
          <div className="media-gallery-caption">{caption ?? horseName}</div>
        </div>
      </div>
    )
  }

  return content
}

export type MediaItem = { type: 'image'; url: string } | { type: 'video'; url: string }

const HORSE_DATA: Record<string, {
  name: string
  breed: string
  gender: string
  badge: string
  img: string
  media?: MediaItem[]
  pricePerShare: number
  totalValue: number
  totalShares: number
  soldShares: number
  winRate: number
  record: string
  yoyReturn: string
  dob: string
  color: string
  location: string
  trainer: string
  insurer: string
  description: string
  sire: string
  dam: string
  races: { name: string; date: string; prize: string; place: number }[]
  healthStatus: string
  lastCheckup: string
  vet: string
  insuranceAmount: string
  healthNote: string
}> = {
  'thunder-strike': {
    name: 'Thunder Strike',
    breed: '纯血赛马 · 3岁',
    gender: '雄性',
    badge: 'champion',
    img: 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=1200&h=800&fit=crop',
    media: [
      { type: 'image', url: 'https://images.unsplash.com/photo-1553284965-83fd3e82fa5a?w=1200&h=800&fit=crop' },
      { type: 'image', url: 'https://images.unsplash.com/photo-1569154941061-e231b4725ef1?w=1200&h=800&fit=crop' },
      { type: 'video', url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4' },
    ],
    pricePerShare: 2500,
    totalValue: 250000,
    totalShares: 100,
    soldShares: 68,
    winRate: 75,
    record: '12胜/16场',
    yoyReturn: '+18.5%',
    dob: '2021年3月15日',
    color: '栗色',
    location: '肯塔基训练场',
    trainer: 'John Smith',
    insurer: "Lloyd's of London",
    description: 'Thunder Strike 是一匹来自冠军血统的纯血赛马，父系为著名种马 Storm King，母系为多次获奖的 Golden Queen。这匹马展现出卓越的速度和耐力，在过去16场比赛中赢得了12场胜利，胜率高达75%。目前由经验丰富的训练师 John Smith 负责训练，在肯塔基州顶级训练场接受专业训练。',
    sire: 'Storm King (冠军种马)',
    dam: 'Golden Queen (多次获奖)',
    races: [
      { name: '肯塔基德比预选赛', date: '2024年1月15日', prize: '$50,000', place: 1 },
      { name: '春季锦标赛', date: '2023年12月3日', prize: '$35,000', place: 1 },
      { name: '秋季经典赛', date: '2023年10月20日', prize: '$15,000', place: 2 },
    ],
    healthStatus: '优秀',
    lastCheckup: '2024年1月1日',
    vet: 'Dr. Sarah Johnson',
    insuranceAmount: '$300,000',
    healthNote: '该马匹已通过完整的兽医检查，健康状况良好，无已知疾病或伤害。定期进行疫苗接种和健康监测。',
  },
  'golden-legacy': {
    name: 'Golden Legacy',
    breed: '纯血赛马 · 4岁',
    gender: '雄性',
    badge: 'hot',
    img: 'https://images.unsplash.com/photo-1551884831-bbf3cdc6469e?w=1200&h=800&fit=crop',
    media: [
      { type: 'image', url: 'https://images.unsplash.com/photo-1551884831-bbf3cdc6469e?w=1200&h=800&fit=crop' },
      { type: 'video', url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4' },
    ],
    pricePerShare: 3800,
    totalValue: 380000,
    totalShares: 100,
    soldShares: 92,
    winRate: 82,
    record: '18胜/22场',
    yoyReturn: '+22%',
    dob: '2020年5月20日',
    color: '枣色',
    location: '肯塔基训练场',
    trainer: 'Michael Brown',
    insurer: "Lloyd's of London",
    description: 'Golden Legacy 是近年来表现最稳定的赛马之一，拥有出色的冲刺与耐力。',
    sire: 'Golden King',
    dam: 'Legacy Queen',
    races: [
      { name: '德比经典赛', date: '2024年2月', prize: '$80,000', place: 1 },
      { name: '冬季杯', date: '2023年11月', prize: '$45,000', place: 1 },
    ],
    healthStatus: '优秀',
    lastCheckup: '2024年1月10日',
    vet: 'Dr. Sarah Johnson',
    insuranceAmount: '$350,000',
    healthNote: '健康状况良好，定期体检。',
  },
  'royal-dynasty': {
    name: 'Royal Dynasty',
    breed: '纯血赛马 · 2岁',
    gender: '雄性',
    badge: 'new',
    img: 'https://images.unsplash.com/photo-1569154941061-e231b4725ef1?w=1200&h=800&fit=crop',
    media: [
      { type: 'image', url: 'https://images.unsplash.com/photo-1569154941061-e231b4725ef1?w=1200&h=800&fit=crop' },
      { type: 'image', url: 'https://images.unsplash.com/photo-1553284966-19b8815c7817?w=1200&h=800&fit=crop' },
    ],
    pricePerShare: 1800,
    totalValue: 180000,
    totalShares: 100,
    soldShares: 34,
    winRate: 67,
    record: '4胜/6场',
    yoyReturn: '-',
    dob: '2022年8月1日',
    color: '黑色',
    location: '佛罗里达训练中心',
    trainer: 'David Lee',
    insurer: 'Equine Insure',
    description: 'Royal Dynasty 年轻有潜力，新上市优质标的。',
    sire: 'Royal Storm',
    dam: 'Dynasty Lady',
    races: [
      { name: '新秀赛', date: '2024年1月', prize: '$20,000', place: 1 },
    ],
    healthStatus: '良好',
    lastCheckup: '2023年12月15日',
    vet: 'Dr. James Wilson',
    insuranceAmount: '$200,000',
    healthNote: '新马，已完成基础健康认证。',
  },
}

const DEFAULT_HORSE = HORSE_DATA['thunder-strike']!

type HorseDetailShape = typeof DEFAULT_HORSE

function mapApiToHorse(api: {
  name: string
  breed?: string
  gender?: string
  badge?: string
  img?: string
  media?: MediaItem[]
  pricePerShare?: number
  totalValue?: number
  totalShares?: number
  soldShares?: number
  winRate?: number
  record?: string
  yoyReturn?: string
  dob?: string
  color?: string
  location?: string
  trainer?: string
  insurer?: string
  description?: string
  sire?: string
  dam?: string
  races?: { name: string; date: string; prize: string; place: number }[]
  healthStatus?: string
  lastCheckup?: string
  vet?: string
  insuranceAmount?: string
  healthNote?: string
}): HorseDetailShape {
  return {
    name: api.name,
    breed: api.breed ?? '',
    gender: api.gender ?? '雄性',
    badge: api.badge ?? 'new',
    img: api.img ?? '',
    media: api.media ?? undefined,
    pricePerShare: api.pricePerShare ?? 0,
    totalValue: api.totalValue ?? 0,
    totalShares: api.totalShares ?? 100,
    soldShares: api.soldShares ?? 0,
    winRate: api.winRate ?? 0,
    record: api.record ?? '',
    yoyReturn: api.yoyReturn ?? '',
    dob: api.dob ?? '',
    color: api.color ?? '',
    location: api.location ?? '',
    trainer: api.trainer ?? '',
    insurer: api.insurer ?? '',
    description: api.description ?? '',
    sire: api.sire ?? '',
    dam: api.dam ?? '',
    races: api.races ?? [],
    healthStatus: api.healthStatus ?? '',
    lastCheckup: api.lastCheckup ?? '',
    vet: api.vet ?? '',
    insuranceAmount: api.insuranceAmount ?? '',
    healthNote: api.healthNote ?? '',
  }
}

export function HorseDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { content: c } = useLocale()
  const [tab, setTab] = useState<TabId>('overview')
  const [shares, setShares] = useState(1)
  const [apiHorse, setApiHorse] = useState<HorseDetailShape | null>(null)

  useEffect(() => {
    if (!MARKETPLACE_API_BASE || !id) {
      setApiHorse(null)
      return
    }
    fetch(`${MARKETPLACE_API_BASE}/marketplace/horses/${encodeURIComponent(id)}`)
      .then((res) => res.json())
      .then((json: { success?: boolean; data?: unknown }) => {
        if (json.success && json.data && typeof json.data === 'object') {
          setApiHorse(mapApiToHorse(json.data as Parameters<typeof mapApiToHorse>[0]))
        } else {
          setApiHorse(null)
        }
      })
      .catch(() => setApiHorse(null))
  }, [id])

  const horse = useMemo((): HorseDetailShape => {
    if (apiHorse) return apiHorse
    const key = id && HORSE_DATA[id] ? id : 'thunder-strike'
    return HORSE_DATA[key] ?? DEFAULT_HORSE
  }, [id, apiHorse])

  const remainingShares = horse.totalShares - horse.soldShares
  const sharesClamped = Math.max(1, Math.min(remainingShares, shares))
  const totalPrice = horse.pricePerShare * sharesClamped

  const getStr = (key: string, def: string) => ((c as Record<string, unknown>)[key] as string | undefined) ?? def
  const championBloodline = c.championBloodline ?? '冠军血统'
  const hot = c.hot ?? '热门'
  const newListing = c.newListing ?? '新上市'
  const pricePerShareLabel = getStr('horseDetailPriceLabel', '单份价格')
  const totalValueLabel = getStr('horseDetailTotalValue', '总价值')
  const soldShareLabel = getStr('horseDetailSoldShare', '已售份额')
  const remainingLabel = getStr('horseDetailRemaining', '剩余')
  const sharesLabel = getStr('horseDetailSharesLabel', '份可购买')
  const buySharesLabel = getStr('horseDetailBuyShares', '购买份额数量')
  const totalLabel = getStr('horseDetailTotal', '总计')
  const buyNow = getStr('horseDetailBuyNow', '立即购买')
  const addToWishlist = getStr('horseDetailWishlist', '添加到收藏')
  const keyMetrics = getStr('horseDetailKeyMetrics', '关键指标')
  const winRateLabel = c.winRate ?? '胜率'
  const recordLabel = getStr('horseDetailRecord', '比赛记录')
  const yoyLabel = getStr('horseDetailYoy', '年化收益')
  const tabOverview = getStr('horseDetailTabOverview', '概览')
  const tabPedigree = getStr('horseDetailTabPedigree', '血统')
  const tabPerformance = getStr('horseDetailTabPerformance', '比赛记录')
  const tabHealth = getStr('horseDetailTabHealth', '健康档案')
  const basicInfo = getStr('horseDetailBasicInfo', '基本信息')
  const dobLabel = getStr('horseDetailDob', '出生日期')
  const genderLabel = getStr('horseDetailGender', '性别')
  const colorLabel = getStr('horseDetailColor', '毛色')
  const locationLabel = getStr('horseDetailLocation', '位置')
  const trainerLabel = getStr('horseDetailTrainer', '训练师')
  const insurerLabel = getStr('horseDetailInsurer', '保险公司')
  const introTitle = getStr('horseDetailIntroTitle', '马匹介绍')
  const pedigreeTitle = getStr('horseDetailPedigreeTitle', '血统图谱')
  const performanceTitle = getStr('horseDetailPerformanceTitle', '比赛记录')
  const healthTitle = getStr('horseDetailHealthTitle', '健康档案')
  const healthStatusLabel = getStr('horseDetailHealthStatus', '健康状态')
  const lastCheckupLabel = getStr('horseDetailLastCheckup', '最近体检')
  const vetLabel = getStr('horseDetailVet', '兽医')
  const insuranceLabel = getStr('horseDetailInsurance', '保险金额')
  const healthCert = getStr('horseDetailHealthCert', '健康证明')

  const badgeLabel = horse.badge === 'champion' ? championBloodline : horse.badge === 'hot' ? hot : newListing
  const badgeIcon = horse.badge === 'champion' ? 'fas fa-crown' : horse.badge === 'hot' ? 'fas fa-fire' : 'fas fa-star'

  return (
    <>
      <section className="horse-detail-section">
        <div className="container">
          <div className="detail-layout">
            <div className="detail-gallery">
              <MediaGallery
                media={horse.media ?? [{ type: 'image', url: horse.img }]}
                horseName={horse.name}
                caption={`${horse.name} / ${horse.sire}`}
              />
            </div>
            <div className="purchase-card">
              <div className="horse-title-section">
                <div style={{ display: 'inline-block', padding: '0.5rem 1rem', background: 'rgba(201, 169, 97, 0.1)', borderRadius: 'var(--radius-full)', color: 'var(--color-primary)', fontWeight: 600, marginBottom: '1rem' }}>
                  <i className={badgeIcon}></i> {badgeLabel}
                </div>
                <h1 className="horse-detail-name">{horse.name}</h1>
                <p style={{ color: 'var(--color-gray-600)', fontSize: '1.1rem' }}>{horse.breed} · {horse.gender}</p>
              </div>
              <div className="price-section">
                <p className="price-label">{pricePerShareLabel}</p>
                <p className="price-amount">${horse.pricePerShare.toLocaleString()}</p>
                <p style={{ color: 'var(--color-gray-600)', fontSize: '0.95rem', marginTop: '0.5rem' }}>{totalValueLabel}: ${horse.totalValue.toLocaleString()} (100份)</p>
              </div>
              <div className="horse-progress" style={{ marginBottom: '2rem' }}>
                <div className="progress-info" style={{ marginBottom: '0.5rem' }}>
                  <span>{soldShareLabel}</span>
                  <span style={{ fontWeight: 700, color: 'var(--color-primary)' }}>{horse.soldShares}/100</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${(horse.soldShares / horse.totalShares) * 100}%` }}></div>
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--color-gray-600)', marginTop: '0.5rem' }}>{remainingLabel} {remainingShares} {sharesLabel}</p>
              </div>
              <div className="shares-section">
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '0.5rem' }}>{buySharesLabel}</label>
                <div className="shares-input-group">
                  <button type="button" className="btn btn-secondary" onClick={() => setShares((s) => Math.max(1, s - 1))}>-</button>
                  <input type="number" className="shares-input" value={sharesClamped} min={1} max={remainingShares} onChange={(e) => setShares(Math.max(1, Math.min(remainingShares, parseInt(e.target.value, 10) || 1)))} />
                  <button type="button" className="btn btn-secondary" onClick={() => setShares((s) => Math.min(remainingShares, s + 1))}>+</button>
                </div>
                <p style={{ fontSize: '1.1rem', fontWeight: 600, color: 'var(--color-secondary)' }}>{totalLabel}: $<span id="totalPrice">{totalPrice.toLocaleString()}</span></p>
              </div>
              <a href={DAPP_URL} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ width: '100%', padding: '1.25rem', fontSize: '1.1rem', marginBottom: '1rem', textDecoration: 'none', display: 'inline-block', textAlign: 'center' }}>
                <i className="fas fa-shopping-cart"></i> {buyNow}
              </a>
              <button type="button" className="btn btn-secondary" style={{ width: '100%', padding: '1rem' }}>
                <i className="fas fa-heart"></i> {addToWishlist}
              </button>
              <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--color-gray-200)' }}>
                <h4 style={{ fontWeight: 700, marginBottom: '1rem', color: 'var(--color-secondary)' }}>{keyMetrics}</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--color-gray-600)' }}>{winRateLabel}</span>
                    <span style={{ fontWeight: 700, color: 'var(--color-primary)' }}>{horse.winRate}%</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--color-gray-600)' }}>{recordLabel}</span>
                    <span style={{ fontWeight: 700 }}>{horse.record}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: 'var(--color-gray-600)' }}>{yoyLabel}</span>
                    <span style={{ fontWeight: 700, color: 'var(--color-accent)' }}>{horse.yoyReturn}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="detail-tabs">
            <div className="tabs-nav">
              {(['overview', 'pedigree', 'performance', 'health'] as TabId[]).map((t) => (
                <button key={t} type="button" className={`tab-btn ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)} data-tab={t}>
                  {t === 'overview' ? tabOverview : t === 'pedigree' ? tabPedigree : t === 'performance' ? tabPerformance : tabHealth}
                </button>
              ))}
            </div>
            {tab === 'overview' && (
              <div className="tab-content active" id="overview">
                <div style={{ background: 'var(--color-white)', padding: '2.5rem', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)' }}>
                  <h3 style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--color-secondary)', marginBottom: '1.5rem' }}>{basicInfo}</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <p className="info-label">{dobLabel}</p>
                      <p className="info-value">{horse.dob}</p>
                    </div>
                    <div className="info-item">
                      <p className="info-label">{genderLabel}</p>
                      <p className="info-value">{horse.gender}</p>
                    </div>
                    <div className="info-item">
                      <p className="info-label">{colorLabel}</p>
                      <p className="info-value">{horse.color}</p>
                    </div>
                    <div className="info-item">
                      <p className="info-label">{locationLabel}</p>
                      <p className="info-value">{horse.location}</p>
                    </div>
                    <div className="info-item">
                      <p className="info-label">{trainerLabel}</p>
                      <p className="info-value">{horse.trainer}</p>
                    </div>
                    <div className="info-item">
                      <p className="info-label">{insurerLabel}</p>
                      <p className="info-value">{horse.insurer}</p>
                    </div>
                  </div>
                  <div style={{ marginTop: '2rem', padding: '1.5rem', background: 'var(--color-background)', borderRadius: 'var(--radius-lg)' }}>
                    <h4 style={{ fontWeight: 700, marginBottom: '1rem', color: 'var(--color-secondary)' }}>{introTitle}</h4>
                    <p style={{ lineHeight: 1.8, color: 'var(--color-gray-700)' }}>{horse.description}</p>
                  </div>
                </div>
              </div>
            )}
            {tab === 'pedigree' && (
              <div className="tab-content active" id="pedigree">
                <div style={{ background: 'var(--color-white)', padding: '2.5rem', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)' }}>
                  <h3 style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--color-secondary)', marginBottom: '1.5rem' }}>{pedigreeTitle}</h3>
                  <div style={{ textAlign: 'center', padding: '3rem', background: 'var(--color-background)', borderRadius: 'var(--radius-lg)' }}>
                    <i className="fas fa-project-diagram" style={{ fontSize: '4rem', color: 'var(--color-primary)', marginBottom: '1rem' }}></i>
                    <p style={{ fontSize: '1.1rem', color: 'var(--color-gray-700)' }}>
                      父系: {horse.sire}<br />
                      母系: {horse.dam}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {tab === 'performance' && (
              <div className="tab-content active" id="performance">
                <div style={{ background: 'var(--color-white)', padding: '2.5rem', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)' }}>
                  <h3 style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--color-secondary)', marginBottom: '1.5rem' }}>{performanceTitle}</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {horse.races.map((r, i) => (
                      <div key={i} style={{ padding: '1.5rem', background: 'var(--color-background)', borderRadius: 'var(--radius-lg)', borderLeft: '4px solid var(--color-primary)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                          <strong style={{ color: 'var(--color-secondary)' }}>{r.name}</strong>
                          <span style={{ background: 'var(--color-primary)', color: 'white', padding: '0.25rem 0.75rem', borderRadius: 'var(--radius-full)', fontSize: '0.875rem', fontWeight: 600 }}>第{r.place}名</span>
                        </div>
                        <p style={{ color: 'var(--color-gray-600)', fontSize: '0.95rem' }}>{r.date} · 奖金 {r.prize}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {tab === 'health' && (
              <div className="tab-content active" id="health">
                <div style={{ background: 'var(--color-white)', padding: '2.5rem', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)' }}>
                  <h3 style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--color-secondary)', marginBottom: '1.5rem' }}>{healthTitle}</h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <p className="info-label">{healthStatusLabel}</p>
                      <p className="info-value" style={{ color: 'var(--color-accent)' }}>{horse.healthStatus}</p>
                    </div>
                    <div className="info-item">
                      <p className="info-label">{lastCheckupLabel}</p>
                      <p className="info-value">{horse.lastCheckup}</p>
                    </div>
                    <div className="info-item">
                      <p className="info-label">{vetLabel}</p>
                      <p className="info-value">{horse.vet}</p>
                    </div>
                    <div className="info-item">
                      <p className="info-label">{insuranceLabel}</p>
                      <p className="info-value">{horse.insuranceAmount}</p>
                    </div>
                  </div>
                  <div style={{ marginTop: '2rem', padding: '1.5rem', background: 'rgba(46, 204, 113, 0.1)', borderRadius: 'var(--radius-lg)', borderLeft: '4px solid var(--color-accent)' }}>
                    <p style={{ color: 'var(--color-accent)', fontWeight: 600, marginBottom: '0.5rem' }}>
                      <i className="fas fa-check-circle"></i> {healthCert}
                    </p>
                    <p style={{ color: 'var(--color-gray-700)' }}>{horse.healthNote}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  )
}
